import { useState } from 'react';
import AnnouncementBar from '@/components/feature/AnnouncementBar';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ProductCard from '@/components/feature/ProductCard';
import { useCart } from '@/hooks/useCart';
import { products } from '@/mocks/products';
import CartItemRow from './components/CartItem';
import OrderSummary from './components/OrderSummary';

export default function CartPage() {
  const { cart, updateQuantity, removeFromCart, clearCart } = useCart();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showCheckoutMsg, setShowCheckoutMsg] = useState(false);

  const safeCart = Array.isArray(cart) ? cart : [];

  const cartProducts = safeCart
    .map((item) => {
      const product = products.find((p) => p.id === item.productId);
      return product ? { ...item, product } : null;
    })
    .filter(Boolean);

  if (safeCart.length === 0) {
    return (
      <div className="min-h-screen bg-background-100">
        <AnnouncementBar />
        <Navbar />
        <main className="pt-28 md:pt-36 pb-12">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-5xl mx-auto">
            {/* Empty Cart State */}
            <div className="bg-background-50 rounded-xl p-8 md:p-16 text-center">
              <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-6">
                <div className="relative">
                  <i className="ri-shopping-cart-2-line text-5xl md:text-6xl text-primary-500" />
                </div>
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-2">
                Your cart is empty!
              </h1>
              <p className="text-sm text-foreground-500 mb-6 max-w-md mx-auto">
                Browse our categories and discover our best deals!
              </p>
              <a
                href="/shop"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors shadow-lg shadow-primary-500/20"
              >
                Start Shopping
              </a>
            </div>

            {/* Recently Viewed */}
            <div className="mt-6 bg-background-50 rounded-xl p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-foreground-950 font-heading">
                  Recently Viewed
                </h2>
                <a
                  href="/shop"
                  className="text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1"
                >
                  See All
                  <i className="ri-arrow-right-s-line" />
                </a>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4">
                {products.slice(0, 6).map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <AnnouncementBar />
      <Navbar />

      <main className="pt-28 md:pt-36 pb-12">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-6">
            Shopping Cart ({safeCart.reduce((s, i) => s + i.quantity, 0)} items)
          </h1>

          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            {/* Cart Items */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-foreground-600">
                  {safeCart.length} product{safeCart.length > 1 ? 's' : ''} in cart
                </p>
                <button
                  type="button"
                  onClick={() => setShowClearConfirm(true)}
                  className="text-sm text-primary-600 hover:text-primary-700 transition-colors flex items-center gap-1"
                >
                  <i className="ri-delete-bin-line" />
                  Clear Cart
                </button>
              </div>

              <div className="space-y-3">
                {cartProducts.map((item) =>
                  item ? (
                    <CartItemRow
                      key={item.productId}
                      item={{ productId: item.productId, quantity: item.quantity }}
                      onUpdateQty={updateQuantity}
                      onRemove={removeFromCart}
                    />
                  ) : null,
                )}
              </div>

              <div className="mt-6">
                <a
                  href="/shop"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  <i className="ri-arrow-left-line" />
                  Continue Shopping
                </a>
              </div>
            </div>

            {/* Order Summary */}
            <div className="w-full lg:w-80 xl:w-96 flex-shrink-0">
              <OrderSummary
                cart={safeCart}
                onCheckout={() => setShowCheckoutMsg(true)}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Clear Cart Confirmation */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground-950/50">
          <div className="bg-background-50 rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-xl text-primary-600" />
            </div>
            <h3 className="text-lg font-bold text-foreground-950 text-center mb-2">
              Clear your cart?
            </h3>
            <p className="text-sm text-foreground-600 text-center mb-6">
              All items will be removed from your cart. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 py-2.5 rounded-lg bg-background-100 text-foreground-800 font-semibold text-sm hover:bg-background-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  clearCart();
                  setShowClearConfirm(false);
                }}
                className="flex-1 py-2.5 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors"
              >
                Clear Cart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Coming Soon */}
      {showCheckoutMsg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground-950/50">
          <div className="bg-background-50 rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-tools-line text-xl text-accent-700" />
            </div>
            <h3 className="text-lg font-bold text-foreground-950 text-center mb-2">
              Coming Soon
            </h3>
            <p className="text-sm text-foreground-600 text-center mb-6">
              The checkout flow with M-Pesa, cards, and delivery tracking will be available in the next phase.
            </p>
            <button
              type="button"
              onClick={() => setShowCheckoutMsg(false)}
              className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}